import { Col, Row, Space } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useBreadcrumb from 'hooks/useBreadcrumb';
import ManifestDashboardHeader from './components/ManifestDashboardHeader';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import ManifestsTable from '../components/manifests-table/ManifestsTable';
import AssignManifestModal from '../components/assign-manifest/AssignManifestModal';
import ArchiveManifestModal from '../components/ArchiveManifestModal';
import { Manifest } from 'store/manifests/types';
import useManifests from 'hooks/useManifests';
import Notification from 'components/ui/base/notification/Notification';
import UnarchiveManifestModal from '../components/UnarchiveManifestModal';
import ManifestsFiltersModal from './components/ManifestsFiltersModal';
import useSession from 'hooks/useSession';
import { hasPermissions } from 'config/roles/hasPermissions';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import Button from 'components/new-ui/base/button/Button';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import useManifestsTable from '../hooks/useManifestsTable';
import ManifestFilterTags from '../components/ManifestFilterTags';
import { getErrorMessage } from 'utils/getErrorMessage';
import useTeams from 'hooks/useTeams';
import useIndividuals from 'hooks/useIndividuals';
import { checkIsAccountsAssociate, checkIsFreightAdmin } from 'config/roles/roles';

const ManifestsDashboard = () => {
    const { listTeams } = useTeams();
    const { listIndividuals } = useIndividuals();
    const { userInfo } = useSession();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const navigate = useNavigate();
    const { archiveManifest, unarchiveManifest } = useManifests();
    const {
        tags,
        queryInputVal,
        appliedParams,
        archived,
        isLoadingManifests,
        data,
        setArchived,
        setQueryInputVal,
        setPage,
        clearAll,
        clearFilter,
        date,
        filters,
        pagination,
        refetchManifests,
        handleTableChange,
        handleDateChange,
        handleFiltersChange,
    } = useManifestsTable();

    const [selectedManifest, setSelectedManifest] = useState<Manifest | null>(null);
    const [archiving, setArchiving] = useState(false);
    const [unarchiving, setUnarchiving] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showAssignManifestModal, setShowAssignManifestModal] = useState(false);
    const [showArchiveManifestModal, setShowArchiveManifestModal] = useState(false);
    const [showUnarchiveManifestModal, setShowUnarchiveManifestModal] = useState(false);

    const { sort, sortDirection } = appliedParams;

    const canCreateManifests = hasPermissions(userInfo, 'manifests', 'CREATE');
    const showTeamFilter = checkIsFreightAdmin(userInfo) || checkIsAccountsAssociate(userInfo);

    const handleManifestClick = (record: Manifest) => {
        const manifestId = record.id;
        navigate(`/manifests/${manifestId}`);
    };

    const handleOpenArchiveModal = (m: Manifest) => {
        setSelectedManifest(m);
        setShowArchiveManifestModal(true);
    };

    const handleOpenUnarchiveModal = (m: Manifest) => {
        setSelectedManifest(m);
        setShowUnarchiveManifestModal(true);
    };

    const handleConfirmArchive = async () => {
        if (!selectedManifest) return;
        try {
            setArchiving(true);
            await archiveManifest(selectedManifest.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Manifest Archived',
                description: 'The manifest has been successfully added to the archive',
            });
            refetchManifests();
            setShowArchiveManifestModal(false);
            setSelectedManifest(null);
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Manifest Archivation Error',
                description: errorMessage || 'Could not arhive manifest. Please try again.',
            });
        } finally {
            setArchiving(false);
        }
    };

    const handleConfirmUnarchive = async () => {
        if (!selectedManifest) return;
        try {
            setUnarchiving(true);
            await unarchiveManifest(selectedManifest.id);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Manifest Unarchived',
                description: 'The manifest will now be activated.',
            });
            refetchManifests();
            setShowUnarchiveManifestModal(false);
            setSelectedManifest(null);
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Manifest Unarchivation Error',
                description: errorMessage || 'Could not activate the manifest. Please try again.',
            });
        } finally {
            setUnarchiving(false);
        }
    };

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Home',
                path: '/home',
            },
            {
                breadcrumbName: 'Manifests',
                path: '/manifests',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    // Prepare teams data for the filters in the modal
    useEffect(() => {
        listTeams({ size: 200 });
        listIndividuals({ size: 200 });
    }, [listTeams, listIndividuals]);

    return (
        <>
            <StyledPageWrapper>
                <Space style={{ width: '100%' }} direction="vertical" size={24}>
                    <ManifestDashboardHeader
                        canCreateManifests={canCreateManifests}
                        status={archived ? 'archived' : 'active'}
                        onStatusChange={(val) => {
                            if (val === 'active') {
                                setArchived(undefined);
                            } else {
                                setArchived(true);
                            }
                        }}
                    />

                    <Row justify="space-between">
                        <Col>
                            <Space direction="vertical">
                                <Space size={8}>
                                    <SearchBar
                                        inputPlaceholder="Search manifests"
                                        value={queryInputVal}
                                        onSearch={(val) => {
                                            setQueryInputVal(val);
                                            setPage(0);
                                        }}
                                        onClear={() => {
                                            setQueryInputVal('');
                                            setPage(0);
                                        }}
                                    />
                                    <Button onClick={() => setShowFilterModal(true)}>Filter</Button>
                                </Space>
                            </Space>
                        </Col>
                        <Col>
                            <DatePicker value={date} onChange={handleDateChange} />
                        </Col>
                    </Row>

                    <Row>
                        <ManifestFilterTags
                            tags={tags}
                            ownerId={appliedParams.ownerId}
                            teamId={appliedParams.teamId}
                            createdBy={appliedParams.createdBy}
                            onClearTag={clearFilter}
                            onClearAll={clearAll}
                        />
                    </Row>

                    <ManifestsTable
                        manifests={data?.list}
                        loading={isLoadingManifests}
                        onArchiveManifest={handleOpenArchiveModal}
                        onUnarchiveManifest={handleOpenUnarchiveModal}
                        onAssignManifest={(manifest) => {
                            setSelectedManifest(manifest);
                            setShowAssignManifestModal(true);
                        }}
                        onRowClick={handleManifestClick}
                        onChange={handleTableChange}
                        paginator={pagination}
                        currentSortDirection={sortDirection}
                        currentSortParam={sort}
                    />
                </Space>
            </StyledPageWrapper>

            <ManifestsFiltersModal
                visible={showFilterModal}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClose={() => setShowFilterModal(false)}
                onClearAll={() => clearAll()}
                onClearFilter={clearFilter}
                optionalFilters={{
                    team: showTeamFilter,
                    assignedTo: true,
                }}
            />

            {selectedManifest && (
                <AssignManifestModal
                    manifest={selectedManifest}
                    open={showAssignManifestModal}
                    onClose={() => setShowAssignManifestModal(false)}
                    onSuccess={() => refetchManifests()}
                />
            )}
            <ArchiveManifestModal
                isArchiving={archiving}
                open={showArchiveManifestModal}
                onConfirm={handleConfirmArchive}
                onClose={() => setShowArchiveManifestModal(false)}
            />
            <UnarchiveManifestModal
                isUnarchiving={unarchiving}
                open={showUnarchiveManifestModal}
                onConfirm={handleConfirmUnarchive}
                onClose={() => setShowUnarchiveManifestModal(false)}
            />
        </>
    );
};

export default ManifestsDashboard;
